import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Beaker, Lock } from "lucide-react";

const BLOOD_TYPES = ["A", "B", "AB", "O", "A-", "B-", "AB-", "O-", "A+", "B+", "AB+", "O+"];
const GENDERS = ["MALE", "FEMALE", "OTHER"];
const PATHS = ["PATH_A"];

export default function PerfumeryTechApp() {
  const { authAxios } = useAuth();
  const { tr } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", birth_date: "", blood_type: "O+", gender: "MALE", fragrance_path: "PATH_A",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authAxios.post("/research/create", form);
      if (r.data?.duplicate_of) {
        toast.info(tr("p5_repeat_notice"));
      } else {
        toast.success("PERSONA Research computed");
      }
      nav(`/research/${r.data.public_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || "Invalid input")
        : (detail || "Error");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-10">
        <div className="pfi-overline inline-flex items-center gap-2"><Beaker size={12} /> PERSONA 5.0 · Build 01</div>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl" data-testid="pt-app-title">{tr("p5_pt_title")}</h1>
        <p className="mt-2 text-[#a9a9b8]">{tr("p5_pt_sub")}</p>
        <div className="mt-3 text-[11px] font-mono text-[#6c6c7a] max-w-2xl leading-relaxed inline-flex items-start gap-2">
          <Lock size={12} className="mt-0.5 text-[#e6c687]" />
          <span>{tr("p5_pt_disclaimer")}</span>
        </div>

        <form onSubmit={submit} className="mt-8 pfi-card p-6 space-y-4" data-testid="pt-app-form">
          <Field label={tr("p5_form_name")}>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="pt-name"
              className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#d4af37]/60" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={tr("p5_form_dob")}>
              <input type="date" required value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                data-testid="pt-dob"
                className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#d4af37]/60" />
            </Field>
            <Field label={tr("p5_form_blood")}>
              <select value={form.blood_type} onChange={(e) => setForm({ ...form, blood_type: e.target.value })}
                data-testid="pt-blood"
                className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#d4af37]/60">
                {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={tr("p5_form_gender")}>
              <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                data-testid="pt-gender"
                className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#d4af37]/60">
                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            {/* Fragrance expression path is an internal implementation
                identifier (PATH_A / PATH_B). It must never be exposed to
                consumers, so the selector is intentionally omitted here.
                The deterministic backend continues to accept the default
                value posted from state below. */}
          </div>
          <button type="submit" disabled={busy} data-testid="pt-submit"
            className="pfi-btn-gold rounded-full px-6 py-3 text-sm disabled:opacity-60">
            {busy ? tr("p5_form_computing") : tr("p5_form_submit")}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="pfi-overline">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
